import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { BarGrid } from '@/components/charts/BarGrid'
import { buildFilterKey } from '@/services/filterEngine'

// Open-ended questions in topline report order
const OPEN_ENDED_QUESTIONS = [
  {
    id: 'OpenEnded_BestThingRegion',
    title: 'Best Thing About Your Region (UNAIDED)',
    text: 'In your opinion, what is the best thing about your home region compared to other regions of Canada and the world?',
  },
  {
    id: 'OpenEnded_LocalConcerns',
    title: 'Local Issues and Concerns (UNAIDED)',
    text: 'When you think of issues or problems that affect the region in which you live, what upsets you most?',
  },
  {
    id: 'OpenEnded_EnvThreats10Yr',
    title: 'Environmental Threats Over Next 10 Years (UNAIDED)',
    text: 'Thinking of threats to the natural environment in your region over the next 10 years, what concerns you most, if anything?',
  },
] as const

// Fixed label order and colors for each question
const BEST_THING_CONFIG: Array<{ label: string; shortLabel?: string; color: string }> = [
  { label: 'Natural environment', shortLabel: 'Nature', color: '#1a472a' },
  { label: 'Recreation and leisure', shortLabel: 'Recreation', color: '#2d6a2f' },
  { label: 'Quiet', color: '#59a14f' },
  { label: 'Community and friendliness', shortLabel: 'Community', color: '#1e3a5f' },
  { label: 'Family and community ties', shortLabel: 'Family ties', color: '#2c5985' },
  { label: 'Cultural diversity', shortLabel: 'Diversity', color: '#4e79a7' },
  { label: 'Convenience and accessibility', shortLabel: 'Convenience', color: '#b33c00' },
  { label: 'Safety and security', shortLabel: 'Safety', color: '#d45500' },
  { label: 'Rural/small town life', shortLabel: 'Rural life', color: '#f28e2c' },
  { label: 'Cost of living', shortLabel: 'Cost', color: '#5c2d51' },
  { label: 'Economic opportunity', shortLabel: 'Economy', color: '#7b4173' },
  { label: 'Healthcare', color: '#2d5f5a' },
  { label: 'Food', color: '#499894' },
  { label: 'No advantages', shortLabel: 'No advantages', color: '#6b7280' },
  { label: 'Prefer local politicians', shortLabel: 'Local politics', color: '#9ca3af' },
]

const LOCAL_CONCERNS_CONFIG: Array<{ label: string; shortLabel?: string; color: string }> = [
  { label: 'Cost of living', shortLabel: 'Cost', color: '#b33c00' },
  { label: 'Affordable housing', shortLabel: 'Housing', color: '#d45500' },
  { label: 'Employment and economy', shortLabel: 'Jobs', color: '#f28e2c' },
  { label: 'Crime and safety', shortLabel: 'Crime', color: '#7a0c0c' },
  { label: 'Homeless people', shortLabel: 'Homeless', color: '#a01a1c' },
  { label: 'Inequality and discrimination', shortLabel: 'Inequality', color: '#e15759' },
  { label: 'Too much immigration', shortLabel: 'Immigration', color: '#ff9d9a' },
  { label: 'Transportation and infrastructure', shortLabel: 'Transport', color: '#1e3a5f' },
  { label: 'Healthcare and social services', shortLabel: 'Healthcare', color: '#2c5985' },
  { label: 'Environment and climate', shortLabel: 'Environment', color: '#2d6a2f' },
  { label: 'Government and politics', shortLabel: 'Government', color: '#7b4173' },
  { label: 'Neighborhood cohesion', shortLabel: 'Neighbors', color: '#499894' },
  { label: 'Need more homeless support', shortLabel: 'Support homeless', color: '#374151' },
]

const ENV_THREATS_CONFIG: Array<{ label: string; shortLabel?: string; color: string }> = [
  { label: 'Climate change', shortLabel: 'Climate', color: '#7a0c0c' },
  { label: 'Extreme weather', shortLabel: 'Weather', color: '#a01a1c' },
  { label: 'Pollution in general', shortLabel: 'Pollution', color: '#4a3f35' },
  { label: 'Water pollution', shortLabel: 'Water', color: '#6b5b4f' },
  { label: 'Air pollution', shortLabel: 'Air', color: '#8c7b6f' },
  { label: 'Deforestation and forest fires', shortLabel: 'Forests', color: '#0f2d1a' },
  { label: 'Loss of biodiversity', shortLabel: 'Biodiversity', color: '#1a472a' },
  { label: 'Overdevelopment/urban sprawl', shortLabel: 'Sprawl', color: '#2d6a2f' },
  { label: 'Industrial activities', shortLabel: 'Industry', color: '#1e3a5f' },
  { label: 'Resource scarcity', shortLabel: 'Resources', color: '#2c5985' },
  { label: 'Population growth', shortLabel: 'Population', color: '#4e79a7' },
  { label: 'Government policy', shortLabel: 'Policy', color: '#5c2d51' },
  { label: 'Lack of respect for environment', shortLabel: 'Disrespect', color: '#7b4173' },
  { label: 'Skepticism/lack of concern', shortLabel: 'Skepticism', color: '#6b7280' },
]

const QUESTION_CONFIGS: Record<string, Array<{ label: string; shortLabel?: string; color: string }>> = {
  'OpenEnded_BestThingRegion': BEST_THING_CONFIG,
  'OpenEnded_LocalConcerns': LOCAL_CONCERNS_CONFIG,
  'OpenEnded_EnvThreats10Yr': ENV_THREATS_CONFIG,
}

export function OpenEndedResponses() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Open-Ended Responses</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Coded responses from open-ended survey questions. Bar width shows percentage who mentioned each topic.
        </p>

        <div className="space-y-10">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            OPEN_ENDED_QUESTIONS.map(({ id, title, text }) => {
              const question = data.questions[id]
              if (!question) {
                return (
                  <div key={id}>
                    <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      {title}
                    </h2>
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                      Data not available
                    </p>
                  </div>
                )
              }

              // Get config for this question
              const config = QUESTION_CONFIGS[id] || []

              // Build columns from config (only include items that exist in data)
              const sampleData = question.data[buildFilterKey('total', 'total')]
              const dataLabels = new Set(sampleData?.responses?.map(r => r.label) || [])

              const columns = config
                .filter(c => dataLabels.has(c.label))
                .map(c => ({
                  id: c.label,
                  label: c.label,
                  shortLabel: c.shortLabel,
                  color: c.color,
                }))

              // Build rows for each break value
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey(
                  'total',
                  selectedBreak,
                  isBreakActive ? value : undefined,
                  selectedProvince
                )
                let questionData = question.data[filterKey]

                if (!questionData && selectedProvince) {
                  const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
                  questionData = question.data[fallbackKey]
                }

                if (!questionData) return null

                // Build values map
                const values: Record<string, number> = {}
                questionData.responses.forEach(r => {
                  values[r.label] = r.percentage
                })

                return {
                  id: value,
                  label: isBreakActive ? getDisplayName(value) : 'Total',
                  values,
                }
              }).filter((row): row is NonNullable<typeof row> => row !== null)

              if (rows.length === 0 || columns.length === 0) {
                return (
                  <div key={id}>
                    <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      {title}
                    </h2>
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                      No data for current filters
                    </p>
                  </div>
                )
              }

              return (
                <BarGrid
                  key={id}
                  title={title}
                  description={text}
                  columns={columns}
                  rows={rows}
                  colorIntensity={false}
                />
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
